import { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import {
  filterRecords,
  sortRecords,
  calculatePaymentMethodTotals,
  calculateUserTotals,
  getDateRange,
  generateXLSXExportData
} from '../utils/depositReportUtils';
import { useApi } from '../Components/Store/ApiContext';

export const useDepositReport = () => {
  const { responseApi } = useApi();

  // Get default date range for last 7 days
  const defaultDateRange = getDateRange('week');

  // Data state
  const [allRecords, setAllRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);

  // Filter state with default values
  const [searchTerm, setSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(defaultDateRange.from);
  const [dateTo, setDateTo] = useState(defaultDateRange.to);
  const [requestIdFilter, setRequestIdFilter] = useState('');
  const [recordTypeFilter, setRecordTypeFilter] = useState('all');
  const [activeQuickFilter, setActiveQuickFilter] = useState('week');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Sorting and pagination state
  const [sortBy, setSortBy] = useState('inputDateTime');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // API call to get real deposit collection data
  const fetchDepositCollectionReport = async (filters = {}) => {
    console.log('Fetching with filters:', filters);

    try {
      setIsLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();

      // Add filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== '' && value !== 'all') {
          queryParams.append(key, value);
        }
      });
      console.log(queryParams);

      // Use the responseApi function from context with correct endpoint
      const response = await responseApi("get", {}, `report/depositCollection?${queryParams}`);

      // Handle both response.data and direct response structures
      const data = response.data || response;

      if (data?.message === 'success') {
        console.log('API Response:', data.results);

        // Update state with real data
        setAllRecords(data.results.records || []);

        return {
          records: data.results.records || [],
          stats: data.results.stats || {},
          methodTotals: data.results.methodTotals || {},
          userTotals: data.results.userTotals || {}
        };
      } else {
        throw new Error(data?.message || 'Failed to fetch deposit collection report');
      }
    } catch (error) {
      console.error('Error fetching deposit collection report:', error);
      setError(error.message || 'Failed to fetch data');

      // Set empty data instead of falling back to mock
      setAllRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Reload data when filters change (debounced)
  useEffect(() => {
    console.log(userFilter);
    const loadData = async () => {
      try {
        await fetchDepositCollectionReport({
          searchTerm,
          userFilter,
          dateFrom,
          dateTo,
          requestIdFilter,
          recordTypeFilter
        });
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    loadData();
  }, [searchTerm, userFilter, dateFrom, dateTo, requestIdFilter, recordTypeFilter]);

  // Filter and sort records
  const filteredRecords = useMemo(() => {
    const filtered = filterRecords(
      allRecords,
      searchTerm,
      userFilter,
      dateFrom,
      dateTo,
      requestIdFilter,
      recordTypeFilter
    );
    return sortRecords(filtered, sortBy, sortOrder);
  }, [allRecords, searchTerm, userFilter, dateFrom, dateTo, requestIdFilter, recordTypeFilter, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRecords.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRecords, currentPage, itemsPerPage]);

  // Calculate totals
  const paymentMethodTotals = useMemo(() => {
    return calculatePaymentMethodTotals(filteredRecords);
  }, [filteredRecords]);

  const userTotals = useMemo(() => {
    return calculateUserTotals(filteredRecords);
  }, [filteredRecords]);

  // Calculate stats
  const stats = useMemo(() => {
    const deposits = filteredRecords.filter(record => record.recordType === 'deposit');
    const refunds = filteredRecords.filter(record => record.recordType === 'refund');

    const totalCollected = deposits.reduce((sum, record) => sum + record.depositAmount, 0);
    const totalRefunds = Math.abs(refunds.reduce((sum, record) => sum + record.depositAmount, 0));
    const netAmount = totalCollected - totalRefunds;
    const uniqueUsers = [...new Set(filteredRecords.map(record => record.userName))];
    const averageDeposit = deposits.length > 0 ? totalCollected / deposits.length : 0;

    return {
      totalRecords: filteredRecords.length,
      totalCollected,
      totalRefunds,
      netAmount,
      activeAgents: uniqueUsers.length,
      averageDeposit
    };
  }, [filteredRecords]);

  // Get unique users for filter dropdown
  const uniqueUsers = useMemo(() => {
    return [...new Set(allRecords.map(record => record.userName))].sort();
  }, [allRecords]);

  // Check if filters are active (excluding default date range)
  const hasActiveFilters = useMemo(() => {
    const defaultRange = getDateRange('week');
    const hasNonDefaultFilters = !!(searchTerm || userFilter || requestIdFilter || recordTypeFilter !== 'all');
    const hasCustomDateRange = dateFrom !== defaultRange.from || dateTo !== defaultRange.to;
    
    return hasNonDefaultFilters || hasCustomDateRange;
  }, [searchTerm, userFilter, dateFrom, dateTo, requestIdFilter, recordTypeFilter]);

  // Handlers
  const handleRefresh = async () => {
    try {
      await fetchDepositCollectionReport({
        searchTerm,
        userFilter,
        dateFrom,
        dateTo,
        requestIdFilter,
        recordTypeFilter
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const handleExport = () => {
    setIsExporting(true);
    try {
      // Generate XLSX data
      const workbookData = generateXLSXExportData(filteredRecords, paymentMethodTotals, userTotals, stats);
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      
      // Add sheets
      Object.entries(workbookData).forEach(([sheetName, data]) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });
      
      // Generate file and download
      const fileName = `deposit-collection-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setTimeout(() => setIsExporting(false), 1000);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleQuickDateFilter = (range) => {
    const dateRange = getDateRange(range);
    setDateFrom(dateRange.from);
    setDateTo(dateRange.to);
    setActiveQuickFilter(range);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    const defaultRange = getDateRange('week');
    setSearchTerm('');
    setUserFilter('');
    setDateFrom(defaultRange.from);
    setDateTo(defaultRange.to);
    setRequestIdFilter('');
    setRecordTypeFilter('all');
    setActiveQuickFilter('week');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return {
    // Data
    filteredRecords,
    paginatedRecords,
    paymentMethodTotals,
    userTotals,
    stats,
    uniqueUsers,

    // State
    isLoading,
    isExporting,
    error,

    // Filters
    searchTerm,
    setSearchTerm,
    userFilter,
    setUserFilter,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    requestIdFilter,
    setRequestIdFilter,
    recordTypeFilter,
    setRecordTypeFilter,
    activeQuickFilter,
    showAdvancedFilters,
    setShowAdvancedFilters,
    hasActiveFilters,

    // Sorting and pagination
    sortBy,
    sortOrder,
    currentPage,
    totalPages,
    itemsPerPage,

    // Handlers
    handleRefresh,
    handleExport,
    handleSort,
    handleQuickDateFilter,
    handleClearFilters,
    handlePageChange,

    // API
    fetchDepositCollectionReport,
  };
};

export default useDepositReport;